<script setup lang="ts">
import { computed } from 'vue';
import AppNav from './AppNav.vue';
import DesktopAppFrame from './DesktopAppFrame.vue';
import FlowHeader from './FlowHeader.vue';
import type { ResolvedLayout } from '@app/composables/useResolvedLayout';
import { useHydrated } from '@app/composables/useHydrated';
import { usePlatform } from '@core/platform';

const props = withDefaults(defineProps<{
  variant?: 'default' | 'wide' | 'narrow' | 'full';
  // Resolved layout from useResolvedLayout(), computed once in App.vue and
  // threaded down so there is a single useMediaQuery/useHydrated instance.
  layout: ResolvedLayout;
}>(), {
  variant: 'default',
});

const variantClasses = {
  default: 'max-w-[var(--page-max-width)]',
  wide: 'max-w-[90rem]',
  narrow: 'max-w-[40rem]',
  full: 'w-full',
} as const;

// Desktop web (not Telegram) gets the centered phone-frame embed instead of the
// stretched mobile layout. Only the phone-shaped app chrome ('tabs'/'bare') is
// frame-eligible; 'desktop'/'plain' routes (demos, admin, 404) keep today's
// full-width layout. The frame decision (incl. the isHydrated gate) and the
// bottom-nav / mobile-padding flags now live in the resolver.
const showDesktopFrame = computed(() => props.layout.showDesktopFrame);
const showFramePlaceholder = computed(() => props.layout.showFramePlaceholder);
const showBottomNav = computed(() => props.layout.showBottomNav);
const useMobilePadding = computed(() => props.layout.useMobilePadding);

const containerClass = computed(() => variantClasses[props.variant]);

// The fixed tab bar (AppNav) clears the Telegram client's bottom edge with an
// extra 1rem inset. Decided here, not in AppNav, because <main> reserves the
// same height (see .app-shell). Hydration-gated like the rest of the nav.
const { isTelegram } = usePlatform();
const isHydrated = useHydrated();
const telegramTabInset = computed(() => isHydrated.value && isTelegram);
</script>

<template>
  <!-- Pre-hydration tick: viewport width is still unknown, so paint a neutral
       branded canvas instead of flashing the full-width layout and snapping into
       the desktop phone-frame (#11). Resolves to the real branch below on mount.
       Telegram / full-width routes skip this and render immediately. -->
  <div v-if="showFramePlaceholder" class="paper-grain min-h-screen bg-background" aria-hidden="true" />

  <!-- Desktop web only: centered phone-frame embed on a branded canvas. -->
  <DesktopAppFrame v-else-if="showDesktopFrame">
    <slot />
  </DesktopAppFrame>

  <!-- Mobile web / Telegram / excluded desktop routes: unchanged layout. -->
  <div
    v-else
    class="app-shell paper-grain min-h-screen bg-background flex flex-col"
    :class="{ 'app-shell--tg': telegramTabInset }"
  >
    <AppNav />
    <!-- Flush sticky top bar (back / step progress), OUTSIDE the padded scroll
         <main> so content can never bleed above it. The whole page scrolls here,
         so it self-pins via `sticky top-0`. Renders nothing on routes without a
         parent / multi-step progress. -->
    <FlowHeader class="sticky top-0" />
    <main
:class="[
      'flex-1 w-full mx-auto px-[var(--page-padding-x)] py-[var(--page-padding-y)] md:pb-[var(--page-padding-y)]',
      containerClass,
      // Tab pages reserve the fixed tab bar's real height (its row + its bottom
      // inset, the same vars AppNav sizes itself from) plus the usual page
      // padding, so the last element ends a page-padding above the bar.
      showBottomNav
        ? 'pb-[calc(var(--tab-bar-row)+var(--tab-bar-inset)+var(--page-padding-y))]'
        : 'pb-[var(--page-padding-y)]',
      useMobilePadding && 'pt-2'
    ]">
      <slot />
    </main>
    <slot name="footer" />
  </div>
</template>

<style scoped>
/* Geometry of the fixed mobile tab bar (AppNav), shared by the bar and <main>:
   - row: py-2.5 + the 2rem icon + gap-1.5 + the 1rem label line = 4.625rem (74px).
     AppNav pins its row to exactly this height, so the two can't drift.
   - inset: the home-indicator safe area; in Telegram at least 1rem (the bar's
     long-standing Telegram bottom padding, now also safe-area aware).
     Telegram's iOS WebView reports env(safe-area-inset-bottom) as 0, so the
     client's own inset (--tg-safe-area-inset-bottom, set by the SDK, Bot API 8.0+)
     is read too — without it the labels sit on the home indicator.
   Only the <md tab layout uses them — from md up the bar is hidden. */
.app-shell {
  --tab-bar-row: 4.625rem;
  --tab-bar-inset: max(env(safe-area-inset-bottom, 0px), var(--tg-safe-area-inset-bottom, 0px));
}

.app-shell--tg {
  --tab-bar-inset: max(1rem, env(safe-area-inset-bottom, 0px), var(--tg-safe-area-inset-bottom, 0px));
}
</style>
